
  import { defineComponent, ref, toRef, computed } from 'vue';
  import Chart from '@/ui/components/analytics/displays/charts/Chart.vue';
  import { Dropdown } from '@/ui/components/controls/Dropdown';
  import { hasSomePermission, Permissions, user } from '@/ui/auth/auth';
  import { t } from '@/ui/i18n/i18n';
  import { GUEST_EXTRA_HEIGHT, useCharts } from '@/ui/concerns/analytics/useCharts';
  import { v4 as uuidv4 } from 'uuid';
  import emitter from '@/services/emitter';
  import { LineChartTimeGroupingType } from '@/services/api/resources/analytics-builder/constants';
  import { ChartBasicSeries, ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import { DayOfWeekIndex, getDayOfWeek, getMonthName } from '@/ui/helpers';
  import { useWidgetFormItem } from '@/ui/concerns/analytics/widget/useWidgets';
  import FormItemTitle from '@/ui/components/analytics/displays/FormItemTitle.vue';
  import { useAnalyticsMap } from '@/ui/concerns/analytics/useAnalyticsMap';
  import { getAttributeValue, getChartAttribute } from '@/services/api/resources/analytics-builder/transform';
  import { useMetrics } from '@/ui/concerns/analytics/widget';
  import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';

  interface LineChartProps {
    widget: ReportWidget;
    timeGroup: LineChartTimeGroupingType;
    percentage: boolean;
    data: { labels: Array<string>; series: Array<ChartBasicSeries> };
    showTimeGroup?: boolean;
  }

  export default defineComponent<LineChartProps>({
    components: {
      FormItemTitle,
      Chart,
      Dropdown,
    },
    props: ['widget', 'timeGroup', 'percentage', 'data', 'showTimeGroup'],
    setup(props, context) {
      const chartTimeGroup = ref(props.timeGroup);
      const isDataPercentage = toRef(props, 'percentage');

      const { metric, attribute, key } = props.widget;

      const { isGuest, reportId, report } = useReportInjection();
      const { findMetricLabel } = useMetrics();
      const { getLineChartGroupOptions } = useAnalyticsMap();
      const {
        formatLabels,
        getDefaultChartConfig,
      } = useCharts(context, reportId.value ?? undefined, report.value, props.widget);

      const formatYLabels = (value: number) => formatLabels(value, isDataPercentage.value);

      const groupOptions = getLineChartGroupOptions(metric, attribute);
      const chartTimeGroupOptions = [
        { value: 'day', label: t('analytics.daily') },
        { value: 'week', label: t('analytics.weekly') },
        { value: 'month', label: t('analytics.monthly') },
        { value: 'quarter', label: t('analytics.quarterly') },
        { value: 'year', label: t('analytics.yearly') },
      ].map(option => ({ ...option, disabled: !groupOptions.includes(option.value as LineChartTimeGroupingType) }));

      const onChartTimeGroupChange = (value: LineChartTimeGroupingType) => {
        chartTimeGroup.value = value;
        emitter.emit('analyticsLineChartTimeGroupingChanged', { key, value });
      };

      const isGroupButtonEnabled = hasSomePermission([
        Permissions.MANAGE_ALL_ANALYTICS,
        Permissions.MANAGE_OWN_ANALYTICS,
      ]);

      const formatXLabels = (value: number) => getAttributeValue(
        value,
        getChartAttribute(props.widget.attribute),
        props.widget.attribute!,
      );

      const formatTimeGroupXLabels = (value: number) => {
        switch (props.timeGroup) {
          case 'day':
            return t(`common.${getDayOfWeek(value as DayOfWeekIndex)}`);
          case 'week':
            return `W${value + 1}`;
          case 'month':
            return getMonthName(value, user.value.locale, 'short');
          case 'quarter':
            return `Q${value}`;
          default:
            return value;
        }
      };

      const { isResponseAttributeSelected } = useWidgetFormItem(props.widget);

      const height = computed(() => {
        let px = isResponseAttributeSelected.value
          ? props.showTimeGroup ? 166 : 199
          : props.showTimeGroup ? 200 : 233;

        if (isGuest) {
          px += GUEST_EXTRA_HEIGHT;
        }

        return px;
      });

      const legendId = `analytics-lc-legend-${uuidv4()}`;

      const options = computed(() => getDefaultChartConfig(
        props.data.labels as string[],
        props.data.series.map(series => ({
          ...series,
          type: 'spline',
          name: series.name ?? findMetricLabel(props.widget.metric!),
        })),
        props.showTimeGroup ? formatTimeGroupXLabels : formatXLabels,
        formatYLabels,
        'spline',
        height.value,
        isDataPercentage.value,
        false,
        true,
      ));

      return {
        chartTimeGroup,
        chartTimeGroupOptions,
        onChartTimeGroupChange,
        isGroupButtonEnabled,
        isResponseAttributeSelected,
        legendId,
        options,
      };
    },
  });
